import { Country, CreateTVChannelRequest, CreateTVChannelResponse, TVChannel } from '@/types/model';
import { apiClient, QueryParams, PaginatedResponse } from './apiClient';

export interface TVChannelQueryParams extends QueryParams {
  active?: boolean;
  categoryId?: string;
  countryId?: string;
  tvGuideId?: string;
  adultContent?: boolean;
  kidsContent?: boolean;
}

export interface TVChannelOrderingQueryParams {
  from?: number;
  to?: number;
}

export interface TVChannelOrderingItem {
  id: string;
  channelNumber: number;
  title: string;
  active: boolean;
  locked: boolean;
  country?: Country | null;
}

export interface UpdateTVChannelOrderingRequest {
  channels: Array<{
    id: string;
    number: number;
    locked: boolean;
  }>;
}

export const tvChannelService = {
  /**
   * Get TV channels with pagination, search, and filtering
   */
  async getTVChannels(params: TVChannelQueryParams = {}): Promise<PaginatedResponse<TVChannel>> {
    return apiClient.getPaginated<TVChannel>('/tv-channels', params);
  },

  /**
   * Get a single TV channel by ID
   */
  async getTVChannelById(id: string): Promise<TVChannel> {
    return apiClient.getById<TVChannel>('/tv-channels', id);
  },

  /**
   * Create a new TV channel
   */
  async createTVChannel(tvChannelData: CreateTVChannelRequest): Promise<CreateTVChannelResponse> {
    return apiClient.create<CreateTVChannelRequest, CreateTVChannelResponse>('/tv-channels', tvChannelData);
  },

  /**
   * Update a TV channel
   */
  async updateTVChannel(id: string, tvChannelData: Partial<CreateTVChannelRequest>): Promise<CreateTVChannelResponse> {
    return apiClient.update<Partial<CreateTVChannelRequest>, CreateTVChannelResponse>('/tv-channels', id, tvChannelData);
  },

  /**
   * Delete a TV channel
   */
  async deleteTVChannel(id: string): Promise<{ success: boolean; message: string }> {
    return apiClient.deleteById<{ success: boolean; message: string }>('/tv-channels', id);
  },

  /**
   * Toggle TV channel active status
   */
  async toggleTVChannelStatus(id: string, active: boolean): Promise<CreateTVChannelResponse> {
    return apiClient.toggleStatus<CreateTVChannelResponse>('/tv-channels', id, active);
  },

  /**
   * Get TV channels for ordering UI
   */
  async getTVChannelsOrdering(params: TVChannelOrderingQueryParams = {}): Promise<TVChannelOrderingItem[]> {
    const response = await apiClient.get<unknown>('/tv-channels/ordering', params as QueryParams);
    if (Array.isArray(response)) {
      return response as TVChannelOrderingItem[];
    }
    if (response && typeof response === 'object' && Array.isArray((response as { content?: unknown[] }).content)) {
      return (response as { content: TVChannelOrderingItem[] }).content;
    }
    return [];
  },

  /**
   * Save TV channels ordering
   */
  async updateTVChannelsOrdering(data: UpdateTVChannelOrderingRequest): Promise<{ success: boolean; message: string }> {
    return apiClient.put<{ success: boolean; message: string }>('/tv-channels/ordering', data);
  },
};
